
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { 
  ChartIcon, 
  BanknotesIcon, 
  CalendarIcon, 
  AlertIcon 
} from './BorrowingsIcons';

export const BorrowingsDashboard = () => {
  // Sample data - would be fetched from an API in a real application
  const debtSummary = {
    totalDebt: 5250000000,
    bankBorrowings: 3200000000,
    capitalMarket: 2050000000,
    publicListed: 1500000000,
    privateCredit: 550000000,
    shortTerm: 1800000000,
    longTerm: 3450000000
  };

  const upcomingRepayments = [
    { id: 1, lender: "HDFC Bank", amount: 25000000, dueDate: "2025-05-15", type: "Interest" },
    { id: 2, lender: "SBI", amount: 50000000, dueDate: "2025-05-20", type: "Principal" },
    { id: 3, lender: "Axis Bank", amount: 35000000, dueDate: "2025-06-10", type: "EMI" },
    { id: 4, lender: "ICICI Bank", amount: 40000000, dueDate: "2025-07-05", type: "Interest" }
  ];

  const covenantHealth = [
    { id: 1, name: "Debt-to-Equity Ratio", current: 1.8, threshold: 2.5, status: "healthy" },
    { id: 2, name: "Interest Coverage Ratio", current: 2.1, threshold: 1.5, status: "healthy" },
    { id: 3, name: "Current Ratio", current: 1.2, threshold: 1.0, status: "healthy" },
    { id: 4, name: "Net Worth", current: 8500000000, threshold: 8000000000, status: "healthy" }
  ];

  // Format numbers as currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Debt Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Debt Outstanding
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(debtSummary.totalDebt)}</div>
            <div className="flex items-center mt-2 text-xs">
              <div className="text-muted-foreground">Short Term: {formatCurrency(debtSummary.shortTerm)}</div>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <div className="text-muted-foreground">Long Term: {formatCurrency(debtSummary.longTerm)}</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Bank Borrowings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(debtSummary.bankBorrowings)}</div>
            <Progress 
              className="mt-2" 
              value={(debtSummary.bankBorrowings / debtSummary.totalDebt) * 100}
            />
            <div className="text-xs text-muted-foreground mt-1">
              {Math.round((debtSummary.bankBorrowings / debtSummary.totalDebt) * 100)}% of total debt
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Capital Market Borrowings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(debtSummary.capitalMarket)}</div>
            <div className="flex items-center mt-2 text-xs">
              <div className="text-muted-foreground">Public: {formatCurrency(debtSummary.publicListed)}</div>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <div className="text-muted-foreground">Private: {formatCurrency(debtSummary.privateCredit)}</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Covenant Health
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">Good</div>
            <div className="text-xs text-muted-foreground mt-1">
              All covenants within threshold
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Repayments */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            Upcoming Repayments
          </CardTitle>
          <CardDescription>
            Repayments due in the next 90 days
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Lender</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead>Type</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {upcomingRepayments.map((repayment) => (
                <TableRow key={repayment.id}>
                  <TableCell>{repayment.lender}</TableCell>
                  <TableCell>{formatCurrency(repayment.amount)}</TableCell>
                  <TableCell>{formatDate(repayment.dueDate)}</TableCell>
                  <TableCell>{repayment.type}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Covenant Health */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertIcon className="h-5 w-5" />
            Financial Covenants Health
          </CardTitle>
          <CardDescription>
            Current status of financial covenants
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Covenant</TableHead>
                <TableHead>Current Value</TableHead>
                <TableHead>Threshold</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {covenantHealth.map((covenant) => (
                <TableRow key={covenant.id}>
                  <TableCell>{covenant.name}</TableCell>
                  <TableCell>
                    {covenant.name === "Net Worth" 
                      ? formatCurrency(covenant.current) 
                      : covenant.current}
                  </TableCell>
                  <TableCell>
                    {covenant.name === "Net Worth" 
                      ? formatCurrency(covenant.threshold) 
                      : covenant.threshold}
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      covenant.status === 'healthy' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {covenant.status === 'healthy' ? 'Healthy' : 'Breach'}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
